import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PosCheckoutPageShell from './PosCheckoutPageShell';
import PosCheckoutSummary from './PosCheckoutSummary';
import { useConfig } from '../context/ConfigContext';
import { useCheckoutPageSession } from '../hooks/useCheckoutPageSession';
import { loadingSpinner } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { computeSignupTotal, submitSubscribeRequest } from '../utils/posSignupUtil';
import { sendSignupReceivedEmail } from '../utils/emailServiceUtil';
import { isOpenerReachable } from '../utils/checkoutSessionUtil';
import { planBillingLabel, planSpanLabel } from '../utils/posLandingUtil';
import { subscribeAdminSettings, DEFAULT_ADMIN_SETTINGS } from '../utils/posSubscriptionUtil';
import { CHECKOUT_EVENTS, CHECKOUT_PAGE_PHASES } from '../constants/bookingConst';
import {
  LANDING_BRAND_NAME, LANDING_BRAND_SUFFIX, LANDING_LOGO_URL, LANDING_PAY_APPS,
} from '../constants/posLandingConst';

/**
 * The landing page's paid sign-up checkout, in its own browser tab
 * (/checkout/<session-id>) — the court-booking system's CheckoutPage.
 *
 * Opened by the landing page's checkout dialog (PosLandingSubscribeModal,
 * through hooks/useCheckoutTab) once the visitor's details are in. This tab
 * takes the payment, SUBMITS the subscription request itself, reports it back
 * and closes — the landing page then shows the thank-you.
 *
 * The payment window (POS_SIGNUP_PAY_WINDOW_SECONDS) started when checkout
 * opened; when it runs out this tab reports it and closes, so a receipt is
 * never attached to a checkout left open for hours at a price that may have
 * changed. × backs out to the details, nothing sent.
 *
 * Branded Kawnter (.ps-kawnter), not with any client's theme: a visitor
 * here is not a client yet.
 *
 * @returns {JSX.Element}
 */
function PosLandingPayment() {
  const { sessionId } = useParams();
  const { config } = useConfig();
  const { session, phase, endCheckout } = useCheckoutPageSession(sessionId);
  const [settings, setSettings] = useState(null);
  const [payApp, setPayApp] = useState(LANDING_PAY_APPS[0].key);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => subscribeAdminSettings(
    setSettings,
    () => setSettings(DEFAULT_ADMIN_SETTINGS),
  ), []);

  const order = session?.record || null;
  const brandName = `${LANDING_BRAND_NAME} ${LANDING_BRAND_SUFFIX}`;

  if (phase === CHECKOUT_PAGE_PHASES.ACTIVE && !settings) {
    return loadingSpinner('Preparing your checkout', LANDING_LOGO_URL);
  }

  /**
   * Sends the request with its receipt, then closes this tab — the landing
   * page shows the thank-you.
   * @param {File} receiptFile
   * @returns {Promise<void>}
   */
  const handleSubmit = async (receiptFile) => {
    setSubmitting(true);
    setError('');
    try {
      const request = await submitSubscribeRequest({
        plan: order.plan,
        contact: order.contact,
        addons: order.addons,
        prices: order.prices,
        staffEmails: order.staffEmails,
        payApp,
        receiptFile,
      });
      // The landing page closes this tab the moment it hears SUBMITTED, which
      // used to cut off an email started here. So the saved request goes
      // back with the event and the landing page — which stays open — sends
      // the email. Only when there is no landing page left to do it does
      // this tab send it itself, waiting for it before closing.
      if (isOpenerReachable()) {
        endCheckout(CHECKOUT_EVENTS.SUBMITTED, { request, emailSent: false });
      } else {
        await sendSignupReceivedEmail(request, config);
        endCheckout(CHECKOUT_EVENTS.SUBMITTED, { request, emailSent: true });
      }
    } catch (failure) {
      console.error('Subscription request failed:', failure);
      setError(failure.isUserFacing
        ? failure.message
        : describeWriteError(failure, { what: 'your subscription', path: 'posSignupRequests' }));
      setSubmitting(false);
    }
  };

  /** @returns {JSX.Element|null} the checkout card for the handed-over order */
  const renderCard = () => {
    if (!order) return null;
    const { plan, addons, prices } = order;
    const staffEmails = order.staffEmails || [];
    const { total } = computeSignupTotal(plan, addons, prices);
    const freeStaff = plan.freeStaff || 0;
    const rows = [
      { key: 'price', label: `Subscription (${planSpanLabel(plan)})`, amount: formatMoney(plan.price, config) },
      { key: 'admin', label: '1 admin account', amount: 'Included' },
    ];
    if (staffEmails.length === 0 && freeStaff > 0) {
      rows.push({ key: 'free', label: `${freeStaff} free staff account${freeStaff === 1 ? '' : 's'}`, amount: 'Included' });
    }
    const boxes = [{ key: 'plan', title: `${brandName} — ${plan.name}`, label: planBillingLabel(plan), rows }];
    if (staffEmails.length > 0) {
      // Named on the staff step: the plan's free accounts cover the first ones.
      boxes.push({
        key: 'staff',
        title: 'Staff accounts',
        label: freeStaff > 0 ? `${Math.min(freeStaff, staffEmails.length)} included with your plan` : '',
        rows: staffEmails.map((email, index) => ({
          key: email,
          label: email,
          amount: index < freeStaff ? 'Included' : formatMoney(prices.extraStaffPrice, config),
        })),
      });
    } else if (addons.extraStaff > 0) {
      rows.push({
        key: 'staff',
        label: `Extra staff account${addons.extraStaff > 1 ? ` × ${addons.extraStaff}` : ''}`,
        amount: formatMoney(prices.extraStaffPrice * addons.extraStaff, config),
      });
    }
    if (addons.warehouse > 0) {
      rows.push({
        key: 'warehouse',
        label: `Warehouse / Stock Room${addons.warehouse > 1 ? ` × ${addons.warehouse}` : ''}`,
        amount: formatMoney(prices.warehousePrice * addons.warehouse, config),
      });
    }
    const app = LANDING_PAY_APPS.find((entry) => entry.key === payApp) || LANDING_PAY_APPS[0];

    return (
      <PosCheckoutSummary
        title="Subscription details"
        boxes={boxes}
        total={formatMoney(total, config)}
        instructions={<>Scan the InstaPay QR below — it works with GCash, Maya and any bank app — and pay exactly <strong>{formatMoney(total, config)}</strong>.</>}
        deadline={order.deadline}
        onExpire={() => endCheckout(CHECKOUT_EVENTS.EXPIRED)}
        payPicker={{
          label: 'Choose your payment app',
          options: LANDING_PAY_APPS.map((entry) => ({ key: entry.key, label: entry.label })),
          value: app.key,
          onChange: setPayApp,
          hint: app.how,
        }}
        payTo={(settings.paymentAccountName || '').toUpperCase()}
        qrUrl={settings.paymentQrUrl}
        noQrMessage="The payment QR code is not available right now. Please email us and we will send it to you."
        submitLabel="Submit subscription"
        submitting={submitting}
        error={error}
        onSubmit={handleSubmit}
        onClose={() => endCheckout(CHECKOUT_EVENTS.CANCELLED)}
        closeLabel="Back to details"
      />
    );
  };

  return (
    <PosCheckoutPageShell
      phase={phase}
      brandName={brandName}
      logoUrl={LANDING_LOGO_URL}
      homeHref="/"
      className="ps-kawnter"
      inactiveMessage="We couldn’t find a checkout in progress for this page. Please go back to the pricing section and choose your plan again."
    >
      {renderCard()}
    </PosCheckoutPageShell>
  );
}

export default PosLandingPayment;
